import { createApp, h } from 'vue'
import { createInertiaApp, router as inertiaRouter } from '@inertiajs/vue3'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'
import en from '../locales/en.json'
import th from '../locales/th.json'
import ru from '../locales/ru.json'
import zh from '../locales/zh.json'
import '../css/application.css'

// inertia_rails renders page data as div[data-page]; Inertia v3 JS expects a
// <script type="application/json"> element. Bridge the gap by passing the
// parsed page data directly so getInitialPageFromDOM is never called.
const appEl = document.getElementById('app')
const initialPage = appEl ? JSON.parse(appEl.dataset.page) : undefined
const defaultLang = initialPage?.props?.locale || initialPage?.props?.app_setting?.language || 'en'

const i18n = createI18n({
  legacy: false,
  locale: defaultLang,
  fallbackLocale: 'en',
  messages: { en, th, ru, zh },
})

// Sync vue-i18n's active locale with whatever the server says on every
// Inertia navigation. Without this, the EN/RU switch on the supplier
// portal (and the operator's Settings language toggle on next-visit)
// updates props but vue-i18n stays pinned to the page's initial locale
// because nothing else listens to the page-props change.
inertiaRouter.on('success', (event) => {
  const props = event.detail?.page?.props || {}
  const nextLocale = props.locale || props.app_setting?.language || 'en'
  if (i18n.global.locale.value !== nextLocale) {
    i18n.global.locale.value = nextLocale
  }
})

createInertiaApp({
  page: initialPage,
  resolve: (name) => {
    const pages = import.meta.glob('../Pages/**/*.vue', { eager: true })
    const page = pages[`../Pages/${name}.vue`]
    return page?.default ?? page
  },
  setup({ el, App, props, plugin }) {
    const pinia = createPinia()
    const app = createApp({ render: () => h(App, props) })
    // No-op when VITE_SENTRY_DSN is unset (the import remains a tiny shim).
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_GIT_SHA || undefined,
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
        sendDefaultPii: false,
        // Filter third-party noise we can't fix: Telegram WebApp SDK
        // throws "Error invoking postEvent: Method not found" when the
        // public catalog opens inside Telegram's in-app browser on an
        // older client missing a given method. Not actionable on our side.
        ignoreErrors: [
          /Error invoking postEvent: Method not found/,
          /ResizeObserver loop limit exceeded/,
          /ResizeObserver loop completed with undelivered notifications/
        ]
      })
    }
    app
      .use(plugin)
      .use(pinia)
      .use(i18n)
      .mount(el)

    // Finish the boot-progress bar once Vue is mounted.
    const boot = document.getElementById('boot-progress')
    if (boot) {
      boot.classList.add('done')
      setTimeout(() => boot.remove(), 300)
    }
  },
  // Color is overridden by #nprogress .bar { background: var(--accent) !important }
  // in application.css so it always matches the active palette.
  progress: { color: 'var(--accent)' },
})
